// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ** API Imports
import axios from '@src/service/axios';
// ** Toast Import
import { toast } from 'react-toastify';

// ─── Leave Category CRUD ───────────────────────────────────────────────────────

export const getLeaveCategory = createAsyncThunk('rulesLeaveRuleSlice/getLeaveCategory', async () => {
    try {
        let response = await axios.get('/LeaveCategory', {
            headers: { 'Content-Type': 'application/json' }
        });
        return Promise.resolve(response?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getLeaveCategoryOption = createAsyncThunk('rulesLeaveRuleSlice/getLeaveCategoryOption', async () => {
    try {
        let response = await axios.get('/LeaveCategory?option=true', {
            headers: { 'Content-Type': 'application/json' }
        });
        return Promise.resolve(response?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const createLeaveCategory = createAsyncThunk('rulesLeaveRuleSlice/createLeaveCategory', async (data, { dispatch }) => {
    try {
        let response = await axios.post('/LeaveCategory', data, {
            headers: { 'Content-Type': 'application/json' }
        });
        toast.success(response?.data?.message || 'Leave Category added successfully.');
        await dispatch(getLeaveCategory());
        return Promise.resolve(response);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const updateLeaveCategory = createAsyncThunk('rulesLeaveRuleSlice/updateLeaveCategory', async (data, { dispatch }) => {
    try {
        let response = await axios.put('/LeaveCategory/' + data?.id, data?.data, {
            headers: { 'Content-Type': 'application/json' }
        });
        toast.success(response?.data?.message || 'Leave Category updated successfully.');
        await dispatch(getLeaveCategory());
        return Promise.resolve(response);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const deleteLeaveCategory = createAsyncThunk('rulesLeaveRuleSlice/deleteLeaveCategory', async (deleteId, { dispatch }) => {
    try {
        let response = await axios.delete('/LeaveCategory/' + deleteId, {
            headers: { 'Content-Type': 'application/json' }
        });
        toast.success(response?.data?.message || 'Leave Category deleted successfully.');
        await dispatch(getLeaveCategory());
        return Promise.resolve(response);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const updateLeaveCategoryStatus = createAsyncThunk('rulesLeaveRuleSlice/updateLeaveCategoryStatus', async (id, { dispatch }) => {
    try {
        let response = await axios.put('/LeaveCategory/status/' + id, {
            headers: { 'Content-Type': 'application/json' }
        });
        toast.success(response?.data?.message || 'Status updated successfully.');
        dispatch(getLeaveCategory());
        return Promise.resolve(response);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

// ─── Leave Category — Employee Assignment ──────────────────────────────────────
// GET  /LeaveCategory/assigned-employee/{id}
// POST /LeaveCategory/assign-policy  { employeeIds: [...], leaveCategories: [...] }

export const getAssignedLeaveCategoryEmployees = createAsyncThunk(
    'rulesLeaveRuleSlice/getAssignedLeaveCategoryEmployees',
    async (leaveCategoryId) => {
        try {
            let response = await axios.get('/LeaveCategory/assigned-employee/' + leaveCategoryId, {
                headers: { 'Content-Type': 'application/json' }
            });
            return Promise.resolve(response?.data);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong fetching assigned employees!');
            return Promise.reject(err);
        }
    }
);

export const bulkAssignLeaveCategory = createAsyncThunk('rulesLeaveRuleSlice/bulkAssignLeaveCategory', async (data, { dispatch }) => {
    // data shape: { employeeIds: [...], leaveCategories: [...] }
    try {
        let response = await axios.post('/LeaveCategory/assign-policy', data, {
            headers: { 'Content-Type': 'application/json' }
        });
        toast.success(response?.data?.message || 'Leave category assigned successfully.');
        await dispatch(getLeaveCategory());
        return Promise.resolve(response);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong assigning leave category!');
        return Promise.reject(err);
    }
});

// ─── Leave Rules CRUD ──────────────────────────────────────────────────────────

// fetch all leave rules for rules module
export const getLeaveRules = createAsyncThunk('rulesLeaveRuleSlice/getLeaveRules', async () => {
    try {
        let response = await axios.get('/LeaveRules', {
            headers: { 'Content-Type': 'application/json' }
        });
        return Promise.resolve(response?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong fetching leave rules!');
        return Promise.reject(err);
    }
});

// delete a rule and refresh list
export const deleteLeaveRule = createAsyncThunk(
    'rulesLeaveRuleSlice/deleteLeaveRule',
    async (deleteId, { dispatch }) => {
        try {
            let response = await axios.delete('/LeaveRules/' + deleteId, {
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Leave rule deleted successfully.');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong deleting leave rule!');
            return Promise.reject(err);
        }
    }
);

// fetch leave rules options for dropdowns (if needed)
export const getLeaveRuleOption = createAsyncThunk(
    'rulesLeaveRuleSlice/getLeaveRuleOption',
    async () => {
        try {
            let response = await axios.get('/LeaveRules?option=true', {
                headers: { 'Content-Type': 'application/json' }
            });
            return Promise.resolve(response?.data);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong fetching leave rule options!');
            return Promise.reject(err);
        }
    }
);

// fetch single rule by id for editing
export const getLeaveRuleById = createAsyncThunk(
    'rulesLeaveRuleSlice/getLeaveRuleById',
    async (id) => {
        try {
            let response = await axios.get('/LeaveRules/' + id, {
                headers: { 'Content-Type': 'application/json' }
            });
            return Promise.resolve(response?.data);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong fetching leave rule!');
            return Promise.reject(err);
        }
    }
);

// update existing rule
export const updateLeaveRule = createAsyncThunk(
    'rulesLeaveRuleSlice/updateLeaveRule',
    async (data, { dispatch }) => {
        try {
            let response = await axios.put('/LeaveRules/' + data?.id, data?.data, {
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Leave rule updated successfully.');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong updating leave rule!');
            return Promise.reject(err);
        }
    }
);

// toggle status if API exists
export const updateLeaveRuleStatus = createAsyncThunk(
    'rulesLeaveRuleSlice/updateLeaveRuleStatus',
    async (id, { dispatch }) => {
        try {
            let response = await axios.put('/LeaveRules/status/' + id, {
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Status updated successfully');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong updating status!');
            return Promise.reject(err);
        }
    }
);

// create new rule (rules form uses only create)
export const createLeaveRule = createAsyncThunk(
    'rulesLeaveRuleSlice/createLeaveRule',
    async (data, { dispatch }) => {
        try {
            let response = await axios.post('/LeaveRules', data, {
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Leave rule added successfully.');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong creating leave rule!');
            return Promise.reject(err);
        }
    }
);

//fetch employees for bulk assign modal
export const getEmployee = createAsyncThunk('rulesLeaveRuleSlice/getEmployee', async () => {
    try {
        let response = await axios.get('/Employee', {
            headers: {
                'Content-Type': 'application/json'
            }
        });
         
        return Promise.resolve(response?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

//Create policy and assign to multiple employees
export const createPolicyAndAssign = createAsyncThunk(
    'rulesLeaveRuleSlice/createPolicyAndAssign',
    async (data, { dispatch }) => {
        try {
            let response = await axios.post('/LeaveRules/assign-policy', data, {
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Policy assigned successfully.');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong assigning policy!');
            return Promise.reject(err);
        }
    }
);

export const getAssignedLeaveRuleEmployees = createAsyncThunk(
    'rulesLeaveRuleSlice/getAssignedLeaveRuleEmployees',
    async ({ id, leaveCategoryId }) => {
        try {
            let response = await axios.get('/LeaveRules/assigned-employee/' + id, {
                params: {
                    leaveCategoryId
                },
                headers: { 'Content-Type': 'application/json' }
            });
            return Promise.resolve(response?.data);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong fetching assigned employees!');
            return Promise.reject(err);
        }
    }
);

export const bulkAssignLeaveRule = createAsyncThunk(
    'rulesLeaveRuleSlice/bulkAssignLeaveRule',
    async (data, { dispatch }) => {
        try {
            const { leaveCategoryId, ...payload } = data;
            let response = await axios.post('/LeaveRules/assign-leave-rule', payload, {
                params: {
                    leaveCategoryId
                },
                headers: { 'Content-Type': 'application/json' }
            });
            toast.success(response?.data?.message || 'Leave rule assigned successfully.');
            await dispatch(getLeaveRules());
            return Promise.resolve(response);
        } catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong assigning leave rule!');
            return Promise.reject(err);
        }
    }
);

export const getApprovalChainOptions = createAsyncThunk(
    'rulesLeaveRuleSlice/getApprovalChainOptions',
    async () => {
        try {   
            let response = await axios.get('/LeaveRules/approval-chain-options', {
                headers: { 'Content-Type': 'application/json' }
            });
            return Promise.resolve(response?.data);
        }
        catch (err) {
            console.log('err', err);
            toast.error(err?.response?.data?.message || 'Something went wrong fetching approval chain options!');
            return Promise.reject(err);
        }
        }
)


export const rulesLeaveRuleSlice = createSlice({
    name: 'rulesLeaveRuleSlice',
    initialState: {
        // Category
        leaveCategory: [],
        leaveCategoryOption: [],
        // Rules
        leaveRules: [],
        leaveRuleOption: [],
        leaveRuleDetail: null,
        approvalChainOptions: [],
        // Misc
        employeeList: []
    },
    extraReducers: (builder) => {
        // Category
        builder.addCase(getLeaveCategory.fulfilled, (state, action) => {
            state.leaveCategory = action?.payload;
        });
        builder.addCase(getLeaveCategoryOption.fulfilled, (state, action) => {
            state.leaveCategoryOption = action?.payload;
        });
        // Rules
        builder.addCase(getLeaveRules.fulfilled, (state, action) => {
            state.leaveRules = action?.payload;
        });
        builder.addCase(getLeaveRuleOption.fulfilled, (state, action) => {
            state.leaveRuleOption = action?.payload;
        });
        builder.addCase(getLeaveRuleById.fulfilled, (state, action) => {
            state.leaveRuleDetail = action?.payload;
        });
        builder.addCase(getApprovalChainOptions.fulfilled, (state, action) => {
            state.approvalChainOptions = action?.payload;
        });
        // Misc
        builder.addCase(getEmployee.fulfilled, (state, action) => {
            state.employeeList = action?.payload;
        });
    }
});

export default rulesLeaveRuleSlice.reducer;
